import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Save, Pencil, Trash2, Plus, RefreshCw, DollarSign,
  TrendingUp, TrendingDown, Calculator, EyeOff, Eye
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const tokens = [
  "USDT", "USDC", "BTC", "ETH", "SOL", "BNB", "XRP", "ADA", "DOGE", "AVAX",
  "MATIC", "DOT", "LINK", "UNI", "ATOM", "LTC", "TRX", "DAI", "BUSD"
];

const platforms = [
  "Binance", "Coinbase", "Kraken", "Bybit", "OKX", "KuCoin", "Huobi", "Gate.io",
  "Bitfinex", "Gemini", "Bitstamp", "Crypto.com", "FTX", "Bithumb", "Upbit",
  "Uniswap", "SushiSwap", "PancakeSwap", "Curve", "1inch", "dYdX", "TraderJoe", "Flux4af",
  "Fintech Platform",
  "Zyphora Platform"
];

const txTypes = [
  { value: "incoming", label: "Входящая (пополнение)" },
  { value: "outgoing", label: "Исходящая (вывод)" },
  { value: "buy", label: "Покупка" },
  { value: "sell", label: "Продажа" },
  { value: "swap", label: "Обмен" },
  { value: "stake", label: "Стейкинг" },
  { value: "unstake", label: "Анстейкинг" },
  { value: "notification", label: "Уведомление" },
];

const emptyTx = {
  amount: "",
  token: "USDT",
  risk_level: "low",
  notes: "",
  profit_loss: "0",
  platform: "Binance",
  tx_type: "incoming",
  detailed_description: ""
};

const AdminAddressEdit = ({ user }) => {
  const { addressId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [editingTx, setEditingTx] = useState(null);
  const [deleteTxId, setDeleteTxId] = useState(null);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [newBalance, setNewBalance] = useState("");
  const [addTxDialog, setAddTxDialog] = useState(false);
  const [newTx, setNewTx] = useState({...emptyTx});

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/addresses/${addressId}/transactions`,
        { withCredentials: true }
      );
      setAddress(response.data.address);
      setTransactions(response.data.transactions);
      setNewBalance(response.data.address.total_balance?.toString() || "0");
    } catch (error) {
      toast.error("Ошибка загрузки");
      navigate("/admin/addresses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [addressId]);

  const handleUpdateTransaction = async () => {
    if (!editingTx) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/transactions/${editingTx.tx_id}`,
        {
          amount: editingTx.amount,
          token: editingTx.token,
          risk_level: editingTx.risk_level,
          notes: editingTx.notes,
          profit_loss: parseFloat(editingTx.profit_loss) || 0
        },
        { withCredentials: true }
      );
      toast.success("Транзакция обновлена");
      setEditingTx(null);
      fetchData();
    } catch (error) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      await axios.delete(`${API}/admin/transactions/${deleteTxId}`, { withCredentials: true });
      toast.success("Транзакция удалена");
      setDeleteTxId(null);
      fetchData();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const handleDeleteHistory = async (historyIndex) => {
    if (!window.confirm("Удалить эту запись из истории?")) return;
    
    try {
      await axios.delete(
        `${API}/admin/addresses/${addressId}/history/${historyIndex}`,
        { withCredentials: true }
      );
      toast.success("Запись удалена из истории");
      fetchData();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const handleSyncHistory = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/admin/addresses/${addressId}/sync-history`,
        {},
        { withCredentials: true }
      );
      toast.success(`Синхронизировано. Удалено: ${response.data.removed_entries}`);
      fetchData();
    } catch (error) {
      toast.error("Ошибка синхронизации");
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateBalance = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/addresses/${addressId}/recalculate`,
        {},
        { withCredentials: true }
      );
      toast.success(`Баланс пересчитан: $${response.data.total_balance?.toLocaleString()}`);
      fetchData();
    } catch (error) {
      toast.error("Ошибка пересчёта");
    } finally {
      setSaving(false);
    }
  };

  const handleSetBalance = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/addresses/${addressId}/balance`,
        { total_balance: parseFloat(newBalance) },
        { withCredentials: true }
      );
      toast.success("Баланс обновлён");
      setBalanceDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Ошибка обновления");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTransaction = async () => {
    // Для уведомлений разрешаем нулевую сумму
    if (newTx.tx_type !== "notification" && (!newTx.amount || parseFloat(newTx.amount) <= 0)) {
      toast.error("Укажите сумму транзакции");
      return;
    }
    
    // Для исходящих транзакций profit_loss должен быть отрицательным
    let finalProfitLoss = parseFloat(newTx.profit_loss) || 0;
    if (newTx.tx_type === "outgoing" || newTx.tx_type === "sell") {
      // Если введено положительное значение, делаем его отрицательным
      finalProfitLoss = -Math.abs(finalProfitLoss);
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/addresses/${addressId}/transactions`,
        {
          amount: newTx.amount,
          token: newTx.token,
          risk_level: newTx.risk_level,
          notes: newTx.notes,
          profit_loss: finalProfitLoss,
          platform: newTx.platform,
          tx_type: newTx.tx_type,
          detailed_description: newTx.detailed_description
        },
        { withCredentials: true }
      );
      toast.success("Транзакция добавлена");
      setAddTxDialog(false);
      setNewTx({...emptyTx});
      fetchData();
    } catch (error) {
      toast.error("Ошибка создания транзакции");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (tx) => {
    setEditingTx({
      ...tx,
      profit_loss: tx.profit_loss?.toString() || tx.amount || "0"
    });
  };

  if (loading) {
    return (
      <AdminLayout user={user}>
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  // Calculate totals
  const totalProfit = transactions.reduce((sum, tx) => {
    const pl = parseFloat(tx.profit_loss) || parseFloat(tx.amount) || 0;
    return sum + pl;
  }, 0);

  return (
    <AdminLayout user={user}>
      <div className="space-y-6" data-testid="admin-address-edit">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/addresses")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Назад
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Редактирование адреса</h1>
              <p className="font-mono text-sm text-slate-500 truncate max-w-[400px]">
                {address?.address}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={handleSyncHistory}
              disabled={saving}
              data-testid="sync-history-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Синхронизировать
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRecalculateBalance}
              disabled={saving}
              data-testid="recalculate-btn"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Пересчитать баланс
            </Button>
            <Button 
              variant="outline"
              onClick={() => setBalanceDialog(true)}
              data-testid="set-balance-btn"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Изменить баланс
            </Button>
          </div>
        </div>

        {/* Balance Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Текущий баланс</p>
                <p className="text-3xl font-bold text-slate-900">
                  ${address?.total_balance?.toLocaleString() || 0}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Hide Balance Toggle */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {address?.hide_balance ? (
                      <EyeOff className="w-4 h-4 text-slate-500" />
                    ) : (
                      <Eye className="w-4 h-4 text-slate-500" />
                    )}
                    <span className="text-sm text-slate-600">Скрыть баланс</span>
                  </div>
                  <Switch
                    checked={address?.hide_balance || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await axios.put(
                          `${API}/admin/addresses/${addressId}`,
                          { hide_balance: checked },
                          { withCredentials: true }
                        );
                        toast.success(checked ? "Баланс скрыт" : "Баланс показан");
                        fetchData();
                      } catch (error) {
                        toast.error("Ошибка обновления");
                      }
                    }}
                    data-testid="hide-balance-toggle"
                  />
                </div>
                {/* Hide Trading Balance Toggle */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {address?.hide_trading_balance ? (
                      <EyeOff className="w-4 h-4 text-amber-600" />
                    ) : (
                      <Eye className="w-4 h-4 text-amber-600" />
                    )}
                    <span className="text-sm text-amber-700">Скрыть баланс торговли</span>
                  </div>
                  <Switch
                    checked={address?.hide_trading_balance || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await axios.put(
                          `${API}/admin/addresses/${addressId}`,
                          { hide_trading_balance: checked },
                          { withCredentials: true }
                        );
                        toast.success(checked ? "Баланс торговли скрыт" : "Баланс торговли показан");
                        fetchData();
                      } catch (error) {
                        toast.error("Ошибка обновления");
                      }
                    }}
                    data-testid="hide-trading-balance-toggle"
                  />
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Транзакций</p>
                  <p className="text-xl font-semibold">{transactions.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Сумма P&L</p>
                  <p className={`text-xl font-semibold ${totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {totalProfit >= 0 ? '+' : ''}{totalProfit.toLocaleString(undefined, {maximumFractionDigits: 2})}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Транзакции ({transactions.length})</CardTitle>
            <Button 
              onClick={() => setAddTxDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-tx-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Добавить транзакцию
            </Button>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Нет транзакций</p>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => {
                  const profitLoss = parseFloat(tx.profit_loss) || parseFloat(tx.amount) || 0;
                  const isPositive = profitLoss >= 0;
                  
                  return (
                    <div 
                      key={tx.tx_id} 
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                      data-testid={`tx-row-${tx.tx_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{tx.token}</Badge>
                          <Badge 
                            className={`text-xs ${
                              tx.risk_level === 'low' ? 'bg-emerald-50 text-emerald-700' :
                              tx.risk_level === 'medium' ? 'bg-amber-50 text-amber-700' :
                              'bg-rose-50 text-rose-700'
                            }`}
                          >
                            {tx.risk_level}
                          </Badge>
                          {tx.platform && (
                            <span className="text-xs text-slate-500">{tx.platform}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{tx.notes || "Без описания"}</p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right min-w-[120px]">
                          <div className={`text-lg font-bold flex items-center justify-end gap-1 ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            {isPositive ? '+' : ''}{profitLoss.toLocaleString(undefined, {maximumFractionDigits: 2})}
                          </div>
                          <p className="text-xs text-slate-500">{tx.amount} {tx.token}</p>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(tx)}
                            data-testid={`edit-tx-${tx.tx_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setDeleteTxId(tx.tx_id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-tx-${tx.tx_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trading History Section */}
        {address?.trading_history && address.trading_history.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">История торговли ({address.trading_history.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {address.trading_history.map((trade, idx) => {
                  const profitLoss = trade.profit_loss || 0;
                  const isPositive = profitLoss >= 0;
                  
                  return (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                      data-testid={`history-row-${idx}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{trade.type}</Badge>
                          <Badge 
                            className={`text-xs ${
                              trade.risk_indicator === 'low' ? 'bg-emerald-50 text-emerald-700' :
                              trade.risk_indicator === 'medium' ? 'bg-amber-50 text-amber-700' :
                              'bg-rose-50 text-rose-700'
                            }`}
                          >
                            {trade.risk_indicator}
                          </Badge>
                          {trade.platform && (
                            <span className="text-xs text-slate-500">{trade.platform}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{trade.description || "Без описания"}</p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right min-w-[120px]">
                          <div className={`text-lg font-bold flex items-center justify-end gap-1 ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            {isPositive ? '+' : ''}{profitLoss.toLocaleString(undefined, {maximumFractionDigits: 2})}
                          </div>
                          <p className="text-xs text-slate-500">Баланс: ${trade.balance_after?.toLocaleString() || 0}</p>
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteHistory(idx)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-history-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Transaction Dialog */}
        <Dialog open={!!editingTx} onOpenChange={() => setEditingTx(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Редактировать транзакцию</DialogTitle>
            </DialogHeader>
            
            {editingTx && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Прибыль/Убыток (USD)</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTx({
                        ...editingTx,
                        profit_loss: Math.abs(parseFloat(editingTx.profit_loss) || 0).toString()
                      })}
                      className="text-emerald-600"
                    >
                      <TrendingUp className="w-4 h-4 mr-1" />
                      +
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTx({
                        ...editingTx,
                        profit_loss: (-Math.abs(parseFloat(editingTx.profit_loss) || 0)).toString()
                      })}
                      className="text-rose-600"
                    >
                      <TrendingDown className="w-4 h-4 mr-1" />
                      −
                    </Button>
                    <Input
                      type="number"
                      value={editingTx.profit_loss}
                      onChange={(e) => setEditingTx({ ...editingTx, profit_loss: e.target.value })}
                      className="flex-1"
                      data-testid="input-profit-loss"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    Положительное значение = прибыль, отрицательное = убыток
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Сумма</Label>
                    <Input
                      value={editingTx.amount}
                      onChange={(e) => setEditingTx({ ...editingTx, amount: e.target.value })}
                      data-testid="input-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Токен</Label>
                    <Select 
                      value={editingTx.token} 
                      onValueChange={(v) => setEditingTx({ ...editingTx, token: v })}
                    >
                      <SelectTrigger data-testid="select-token">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {tokens.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Уровень риска</Label>
                  <Select 
                    value={editingTx.risk_level} 
                    onValueChange={(v) => setEditingTx({ ...editingTx, risk_level: v })}
                  >
                    <SelectTrigger data-testid="select-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Низкий (Low)</SelectItem>
                      <SelectItem value="medium">Средний (Medium)</SelectItem>
                      <SelectItem value="high">Высокий (High)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Описание</Label>
                  <Input
                    value={editingTx.notes || ""}
                    onChange={(e) => setEditingTx({ ...editingTx, notes: e.target.value })}
                    placeholder="Описание транзакции"
                    data-testid="input-notes"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTx(null)}>
                Отмена
              </Button>
              <Button 
                onClick={handleUpdateTransaction}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-tx-btn"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Сохранить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Set Balance Dialog */}
        <Dialog open={balanceDialog} onOpenChange={setBalanceDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Изменить баланс</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Новый баланс (USD)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={newBalance}
                    onChange={(e) => setNewBalance(e.target.value)}
                    className="pl-10"
                    placeholder="0"
                    data-testid="input-new-balance"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setBalanceDialog(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleSetBalance}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="confirm-balance-btn"
              >
                Сохранить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Transaction Dialog */}
        <Dialog open={addTxDialog} onOpenChange={setAddTxDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Добавить транзакцию</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Тип транзакции</Label>
                <Select 
                  value={newTx.tx_type} 
                  onValueChange={(v) => setNewTx({ ...newTx, tx_type: v })}
                >
                  <SelectTrigger data-testid="select-tx-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {txTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Сумма (USD)</Label>
                  {(newTx.tx_type === "outgoing" || newTx.tx_type === "sell") && address?.total_balance > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setNewTx({
                        ...newTx,
                        profit_loss: address.total_balance.toString(),
                        amount: address.total_balance.toString()
                      })}
                      className="text-rose-600 h-7 text-xs"
                    >
                      Вывести всё (${address.total_balance?.toLocaleString()})
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNewTx({
                      ...newTx,
                      profit_loss: Math.abs(parseFloat(newTx.profit_loss) || 0).toString()
                    })}
                    className="text-emerald-600"
                  >
                    <TrendingUp className="w-4 h-4 mr-1" />
                    +
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNewTx({
                      ...newTx,
                      profit_loss: (-Math.abs(parseFloat(newTx.profit_loss) || 0)).toString()
                    })}
                    className="text-rose-600"
                  >
                    <TrendingDown className="w-4 h-4 mr-1" />
                    −
                  </Button>
                  <Input
                    type="number"
                    value={newTx.profit_loss}
                    onChange={(e) => setNewTx({ ...newTx, profit_loss: e.target.value })}
                    className="flex-1"
                    placeholder="0"
                    data-testid="input-new-profit-loss"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Сумма</Label>
                  <Input
                    value={newTx.amount}
                    onChange={(e) => setNewTx({ ...newTx, amount: e.target.value })}
                    placeholder="1000"
                    data-testid="input-new-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Токен</Label>
                  <Select 
                    value={newTx.token} 
                    onValueChange={(v) => setNewTx({ ...newTx, token: v })}
                  >
                    <SelectTrigger data-testid="select-new-token">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tokens.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Уровень риска</Label>
                  <Select 
                    value={newTx.risk_level} 
                    onValueChange={(v) => setNewTx({ ...newTx, risk_level: v })}
                  >
                    <SelectTrigger data-testid="select-new-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Низкий (Low)</SelectItem>
                      <SelectItem value="medium">Средний (Medium)</SelectItem>
                      <SelectItem value="high">Высокий (High)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Платформа</Label>
                  <Select 
                    value={newTx.platform} 
                    onValueChange={(v) => setNewTx({ ...newTx, platform: v })}
                  >
                    <SelectTrigger data-testid="select-new-platform">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Описание</Label>
                <Input
                  value={newTx.notes}
                  onChange={(e) => setNewTx({ ...newTx, notes: e.target.value })}
                  placeholder="Покупка 0.5 BTC"
                  data-testid="input-new-notes"
                />
              </div>

              <div className="space-y-2">
                <Label>Подробное описание (видно клиенту при клике)</Label>
                <textarea
                  value={newTx.detailed_description}
                  onChange={(e) => setNewTx({ ...newTx, detailed_description: e.target.value })}
                  placeholder="Подробное описание операции: как были заработаны/потеряны средства, через какие платформы прошли, связь с другими адресами..."
                  className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="input-detailed-description"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddTxDialog(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleCreateTransaction}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-add-tx-btn"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Добавить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTxId} onOpenChange={() => setDeleteTxId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить транзакцию?</AlertDialogTitle>
              <AlertDialogDescription>
                Это действие нельзя отменить.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteTransaction}
                className="bg-rose-600 hover:bg-rose-700"
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAddressEdit;
